import React from 'react';
import { 
  User, Clock, Tag, AlertTriangle, CheckCircle, 
  HelpCircle, MessageSquare, ArrowUpRight, RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';

/**
 * ConversationStatePanel - Displays structured summary on the right side
 * 
 * Shows:
 * - Current Intent
 * - Status
 * - Collected slot_data fields
 * - Returning customer badge
 * - Last updated timestamp
 * - Escalation status
 */
const ConversationStatePanel = ({ state, onRefresh }) => {
  if (!state) return null;

  const statusColors = {
    new: 'bg-gray-500',
    collecting_info: 'bg-blue-500',
    awaiting_more_input: 'bg-yellow-500',
    pending_human: 'bg-orange-500',
    resolved: 'bg-green-500',
    confirmed: 'bg-green-500',
    closed: 'bg-gray-400'
  };

  const statusLabels = {
    new: 'New',
    collecting_info: 'Collecting Info',
    awaiting_more_input: 'Awaiting Input',
    pending_human: 'Pending Human Review',
    resolved: 'Resolved',
    confirmed: 'Confirmed',
    closed: 'Closed'
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const slotData = state.slot_data || {};
  const hasSlotData = Object.keys(slotData).some(k => slotData[k]);

  return (
    <Card className="h-fit" data-testid="conversation-state-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center justify-between">
          <span className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4" />
            Conversation State
          </span>
          {onRefresh && (
            <button 
              onClick={onRefresh}
              className="p-1 hover:bg-muted rounded"
              title="Refresh state"
            >
              <RefreshCw className="w-3 h-3" />
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {/* Handled by Human Badge */}
        {state.handled_by_human && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              <CheckCircle className="w-3 h-3 mr-1" />
              Handled by Human
            </Badge>
          </div>
        )}
        
        {/* Returning Customer Badge */}
        {state.returning_customer && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
              <RefreshCw className="w-3 h-3 mr-1" />
              Returning Customer
            </Badge>
          </div>
        )}

        {/* Status */}
        <div className="space-y-1">
          <span className="text-muted-foreground text-xs uppercase tracking-wider">Status</span>
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${statusColors[state.status] || 'bg-gray-500'}`} />
            <span className="font-medium">{statusLabels[state.status] || state.status}</span>
          </div>
        </div>

        {/* Intent */}
        {state.intent && (
          <div className="space-y-1">
            <span className="text-muted-foreground text-xs uppercase tracking-wider">Intent</span>
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium capitalize">{state.intent.replace('_', ' ')}</span>
            </div>
          </div>
        )}

        {/* Escalation Alert */}
        {state.escalation_required && (
          <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">Escalation Required</span>
            </div>
            {state.escalation_reason && (
              <p className="text-xs text-muted-foreground mt-1">{state.escalation_reason}</p>
            )}
          </div>
        )}

        {/* Collected Data */}
        {hasSlotData && (
          <div className="space-y-2">
            <span className="text-muted-foreground text-xs uppercase tracking-wider">Collected Information</span>
            <div className="space-y-2 p-3 bg-muted/50 rounded-lg">
              {Object.entries(slotData).map(([key, value]) => {
                if (!value) return null;
                return (
                  <div key={key} className="flex items-start gap-2">
                    <CheckCircle className="w-3 h-3 text-emerald-500 mt-1 flex-shrink-0" />
                    <div>
                      <span className="text-muted-foreground capitalize">{key.replace('_', ' ')}:</span>
                      <span className="ml-1 font-medium">{String(value)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Customer Info */}
        {(state.customer_name || state.customer_phone) && (
          <div className="space-y-2">
            <span className="text-muted-foreground text-xs uppercase tracking-wider">Customer</span>
            <div className="space-y-1">
              {state.customer_name && (
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-muted-foreground" />
                  <span>{state.customer_name}</span>
                </div>
              )}
              {state.customer_phone && (
                <div className="text-xs text-muted-foreground pl-6">
                  {state.customer_phone}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Last Activity */}
        <div className="space-y-1 pt-2 border-t">
          <span className="text-muted-foreground text-xs uppercase tracking-wider">Last Activity</span>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Clock className="w-3 h-3" />
            <span>{formatDate(state.last_activity_at)}</span>
          </div>
        </div>

        {/* Structured Summary (if escalated) */}
        {state.structured_summary && (
          <div className="space-y-2 pt-2 border-t">
            <span className="text-muted-foreground text-xs uppercase tracking-wider">Summary</span>
            <div className="p-3 bg-muted rounded-lg text-xs">
              <pre className="whitespace-pre-wrap font-mono">
                {JSON.stringify(state.structured_summary, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ConversationStatePanel;
